import { useQuery } from '@tanstack/react-query';
import { remixApi } from '@tectonic/api-client';
import { ElemasonFragmentProvider } from '../../contexts/ElemasonFragmentContext';
import { Cell } from '../../core/Cell';
import { usePageFragment } from '../../hooks';
import { getCountdownValues } from '../../hooks/useCountdown';
import { useFragmentValue } from '../../hooks/useFragmentValue';
import { useHaloScript } from '../../hooks/useHaloScript';
import { queryKeys } from '../../queryKeys';

import type { ElemasonServiceabilityWidget } from '@tectonic/types';
import type { FC } from 'react';

interface ServiceabilityWidgetProps {
  widget: ElemasonServiceabilityWidget;
}

const options: Intl.DateTimeFormatOptions = {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
};

const ServiceabilityWidget: FC<ServiceabilityWidgetProps> = ({
  widget: { data },
}) => {
  const zipcode = useHaloScript(data?.zipcode);
  const variantId = useHaloScript(data?.variantId);
  const fragment = usePageFragment(data?.fragment);

  const {
    isError,
    isSuccess,
    isLoading,
    isPending,
    data: serviceability,
  } = useQuery({
    enabled: !!variantId,
    queryKey: queryKeys.serviceability(variantId!),
    queryFn: () => remixApi.getServiceability(variantId!, zipcode),
  });
  const fragmentValue = useFragmentValue(fragment);

  const { days, hours, minutes, seconds } = serviceability?.data?.additionalInfo
    .orderInNextSeconds
    ? getCountdownValues(
        serviceability.data.additionalInfo.orderInNextSeconds! * 1000
      )
    : { days: 0, hours: 0, minutes: 0, seconds: 0 };

  return (
    <ElemasonFragmentProvider
      value={fragmentValue({
        isError,
        isSuccess,
        serviceability,
        isLoading: isLoading || isPending,
        dispatchStartDate: serviceability?.data?.additionalInfo.dispatchDates
          ?.min.date
          ? Intl.DateTimeFormat('en-US', options).format(
              new Date(
                serviceability?.data.additionalInfo.dispatchDates?.min.date
              )
            )
          : null,
        dispatchEndDate: serviceability?.data?.additionalInfo.dispatchDates?.max
          .date
          ? Intl.DateTimeFormat('en-US', options).format(
              new Date(
                serviceability?.data.additionalInfo.dispatchDates?.max.date
              )
            )
          : null,
        countdown: {
          days,
          hours,
          minutes,
          seconds,
        },
      })}
    >
      {fragment?.cells.map((cell) => <Cell cell={cell} key={cell.id} />)}
    </ElemasonFragmentProvider>
  );
};

export { ServiceabilityWidget };
